import axios from "axios";
import { BASE_URL } from "../../utils/util";
import { useState, useEffect, ReactNode } from "react";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);

const FlagBadge = ({ children }: { children: ReactNode }) => (
  <>
    <span className="bg-[#FF228C] z-10 whitespace-nowrap absolute text-white px-2 py-[3px] text-[8px] rounded-sm left-[50%] translate-x-[-50%] top-[-10px]">
      {children}
    </span>
    <div
      aria-hidden
      className="absolute z-10 w-0 h-0 box-content border-[6px] border-solid border-transparent border-t-[#FF228C] top-2 left-[50%] translate-x-[-50%]"
    />
  </>
);

const HeaderFlag = ({ value }: { value: string }) => {
  const { data: deviceInquiryTag, isLoading: deviceInquiryTagLoading } = useQuery(
    "메인_기기문의_배지",
    () =>
      axios.get(`${BASE_URL}/home/KVStore/메인_기기문의_배지`).then((res) => res.data.store?.value),
    { cacheTime: Infinity, staleTime: 15 * 60 * 1000 },
  );

  const { data: classLive, isLoading: classLiveLoading } = useQuery(
    "메인_보수교육_LIVE",
    () =>
      axios.get(`${BASE_URL}/home/KVStore/메인_보수교육_LIVE`).then((res) => res.data.store?.value),
    { cacheTime: Infinity, staleTime: 15 * 60 * 1000 },
  );

  const { data: marketTag, isLoading: marketTagLoading } = useQuery(
    "메인_groupbuying",
    () => axios.get(`${BASE_URL}/home/KVStore/groupbuying`).then((res) => res.data.store?.value),
    { cacheTime: Infinity, staleTime: 15 * 60 * 1000 },
  );

  const { data: expoTag, isLoading: expoTagLoading } = useQuery(
    "메인_공동구매관_배지",
    () =>
      axios
        .get(`${BASE_URL}/home/KVStore/메인_공동구매관_배지`)
        .then((res) => res.data.store?.value),
    { cacheTime: Infinity, staleTime: 15 * 60 * 1000 },
  );

  const isDate = (value: string | number) => {
    if (typeof value !== "string" && typeof value !== "number") return false;

    // MARK: 기존 로직에서 'D-1' 같은 문자열도 isDate가 true로 판단되는 문제가 있어서 format을 한정시킵니다.
    // MARK: yyyy-mm-dd | yyyy:mm:dd | yy-mm-dd | yy:mm:dd
    const isoDateRegex = /^(?:\d{4}|\d{2})[-:]\d{2}[-:]\d{2}$/;
    if (!isoDateRegex.test(value.toString())) return false;

    const date = new Date(value.toString());
    return date instanceof Date && !isNaN(date.getTime());
  };

  // 날짜 값이면 카운트다운, 그 외 문자열이면 문자열 그대로 노출
  if (value === "class" && !classLiveLoading && !!classLive)
    return isDate(classLive) ? (
      <Timer dateString={classLive} />
    ) : (
      <FlagBadge>{classLive}</FlagBadge>
    );

  if (value === "market" && !marketTagLoading && !!marketTag)
    return isDate(marketTag) ? (
      <Timer dateString={marketTag} />
    ) : (
      <FlagBadge>{marketTag}</FlagBadge>
    );

  // 기기 문의 배지. KVStore 메인_기기문의_배지 값을 비우면 사라짐
  if (value === "device-inquiry" && !deviceInquiryTagLoading && !!deviceInquiryTag)
    return isDate(deviceInquiryTag) ? (
      <Timer dateString={deviceInquiryTag} />
    ) : (
      <FlagBadge>{deviceInquiryTag}</FlagBadge>
    );

  // 공동구매관 배지. KVStore 메인_공동구매관_배지 값을 비우면 사라짐
  if (value === "expo" && !expoTagLoading && !!expoTag) return <FlagBadge>{expoTag}</FlagBadge>;

  if (value === "chicruiting")
    return (
      <>
        <span className="bg-[#000] z-10 whitespace-nowrap absolute text-white px-2 py-[0px] text-[10px] font-semibold rounded-full right-0 top-0">
          직원 채용
        </span>
        <svg
          className="absolute top-[8px] left-[40px]"
          width="9"
          height="8"
          viewBox="0 0 9 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            id="Vector 4"
            d="M0.177182 6.30678L3.34194 0.424478C3.53208 0.0710823 4.00883 0.000336998 4.29339 0.283293L8.10241 4.07083C8.42481 4.39141 8.28739 4.94013 7.85195 5.07092L0.878171 7.1657C0.366225 7.31947 -0.0760812 6.77752 0.177182 6.30678Z"
            fill="#F06E6F"
          />
        </svg>
      </>
    );

  // 기본적으로 null을 반환하여 JSX 요소가 아닌 경우를 처리
  return null;
};

export default HeaderFlag;

function Timer({ dateString }: { dateString: string | number }) {
  const [timeRemaining, setTimeRemaining] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const targetDate = dayjs(dateString).startOf("day").add(0, "hour");
      const now = dayjs();
      const diff = targetDate.diff(now);
      if (diff <= 0) {
        // 날짜가 이미 지났으면 타이머를 멈춥니다.
        clearInterval(intervalId);
        setTimeRemaining(null);
        return;
      }
      const duration = dayjs.duration(diff);
      setTimeRemaining({
        days: duration.days(),
        hours: duration.hours(),
        minutes: duration.minutes(),
        seconds: duration.seconds(),
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [dateString]);

  if (!timeRemaining) return null;

  return (
    <FlagBadge>
      {timeRemaining.days > 0 && <span>{timeRemaining.days}일&nbsp;</span>}
      {timeRemaining.hours.toString().padStart(2, "0")}:
      {timeRemaining.minutes.toString().padStart(2, "0")}:
      {timeRemaining.seconds.toString().padStart(2, "0")} 남음
    </FlagBadge>
  );
}
